/**
 * TK-38 việc 2 (lee 23/10) — luật THUẦN của trang Vận hành (không React, không API) để kiem-tk38 gọi và phá.
 *
 *   · `tuSo1`: tủ số 1 của tuyến = đứng đầu khi xếp theo `code` (thứ tự tự nhiên: SIM-2 trước SIM-10), rồi `name`, rồi `id`;
 *     tủ ấy chưa có toạ độ ⇒ tủ kế tiếp có toạ độ; không tủ nào có ⇒ null ("Tuyến chưa có vị trí").
 *     Đã tự quyết: bỏ vế "ngày gán" của phiếu — cây tuyến không mang ngày gán; `id` làm vế phân định cuối.
 *   · `locTuyen`: tìm theo tên đầy đủ, mã, phường, tỉnh (không phân biệt hoa thường, bỏ dấu).
 *   · Đơn vị đèn (lee 23/10): I hiển thị **mA** = `i_10ma × 10` (gói card và cột CSDL giữ 0,01 A); PF = `pf_pct / 100`, 2 số lẻ.
 *     NULL ⇒ null (luật số 0, nơi vẽ in "—").
 */
import { daiCapDien, ngayVN, type NgoLich } from './gioCapDien';

export interface TuToaDo { id: string; code: string | null; name: string; lat: number | null; lng: number | null }

const soSanhTuNhien = new Intl.Collator('vi', { numeric: true, sensitivity: 'base' });
const toaDoHopLe = (lat: number | null, lng: number | null) =>
  lat !== null && lng !== null && Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

export function xepTu<T extends TuToaDo>(ds: T[]): T[] {
  return [...ds].sort((a, b) => soSanhTuNhien.compare(a.code ?? '', b.code ?? '') || soSanhTuNhien.compare(a.name, b.name) || a.id.localeCompare(b.id));
}

export function tuSo1<T extends TuToaDo>(ds: T[]): T | null {
  return xepTu(ds).find((c) => toaDoHopLe(c.lat, c.lng)) ?? null;
}

const boDau = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();
export function locTuyen<T extends { tenDayDu: string; code: string | null; phuong: string | null; tinh: string | null }>(ds: T[], q: string): T[] {
  const k = boDau(q.trim());
  if (!k) return ds;
  return ds.filter((r) => [r.tenDayDu, r.code, r.phuong, r.tinh].some((x) => x && boDau(x).includes(k)));
}

/** I của đèn ra mA (card gửi 0,01 A). */
export const iMa = (i_10ma: number | null | undefined): number | null => (i_10ma == null ? null : i_10ma * 10);
/**
 * Vận hành 24/10: lúc ĐO được của một đèn — `sensor_at` chỉ khi đèn có ít nhất một số đo. Card ghi `sensor_at` cả khi đèn KHÔNG trả
 * lời (mọi số NULL) ⇒ hiện "vừa xong" cho một đèn mất liên lạc là sai (luật số 0) ⇒ null ("—").
 */
export const lucCapNhat = (d: { sensor_at?: string | null; u_v?: number | null; i_10ma?: number | null; p_w: number | null; pf_pct?: number | null; dim: number | null }): string | null =>
  [d.u_v, d.i_10ma, d.p_w, d.pf_pct, d.dim].some((v) => v != null) ? d.sensor_at ?? null : null;
/** PF 0,00–1,00 (card gửi %). */
export const pfHai = (pf_pct: number | null | undefined): string | null => (pf_pct == null ? null : (pf_pct / 100).toFixed(2).replace('.', ','));

/** Ba trạng thái ngõ theo tuyến (máy chủ tính, `services/ngoTuyen.ts`) ⇒ lớp màu token + nhãn. null = chưa rõ (XÁM rỗng). */
export type TrangThaiNgo = 'bat' | 'tat' | 'mot-phan';
export interface NgoTuyen { trangThai: TrangThaiNgo | null; tuBat: number; tuNoi: number; tuMat: number }
export const MAU_NGO: Record<TrangThaiNgo | 'chua-ro', { cham: string; nhan: string }> = {
  bat: { cham: 'bg-tot', nhan: 'Đang bật' },
  tat: { cham: 'bg-chu-3', nhan: 'Đang tắt' },
  'mot-phan': { cham: 'bg-canh', nhan: 'Bật một phần' },
  'chua-ro': { cham: 'border border-chu-3 bg-transparent', nhan: 'Chưa rõ' },
};
export const khoaNgo = (n: NgoTuyen | null | undefined): TrangThaiNgo | 'chua-ro' => n?.trangThai ?? 'chua-ro';

/* ── TK-38 việc 4: lọc cây Quản lý đèn + giờ ngõ bật hôm nay theo lịch tủ đang chạy (thuần — kiem-tk38 [4] gọi) ── */
export interface DenTimDuoc { addr: number; stt: number | null; name: string | null }
const tenDenThuan = (d: DenTimDuoc) => d.name?.trim() || (d.stt != null ? `Đèn ${d.stt}` : 'Đèn');

/** Lọc cây theo chữ tìm: giữ tuyến/tủ khớp (kèm con), hoặc nhánh có con khớp. Thuần — bộ kiểm gọi được. */
/**
 * TK-41 §3 (lee 24/10): cây Quản lý đèn mở trang là THU GỌN (chỉ dòng tuyến). Có mục trên URL ⇒ mở ĐÚNG đường dẫn tới nó:
 * `?tu=` ⇒ tuyến của tủ · `?den=` ⇒ tuyến + tủ · `?tuyen=` không cần mở gì (dòng tuyến đã thấy). Thuần.
 */
export function duongMo(cay: { id: string; tu: { id: string }[] }[], chon: { tuyen: string | null; tu: string | null; den: number | null }): Set<string> {
  const mo = new Set<string>();
  const r = chon.tu ? cay.find((x) => x.tu.some((t) => t.id === chon.tu)) : undefined;
  if (r) { mo.add(r.id); if (chon.den !== null) mo.add(chon.tu!); }
  return mo;
}

export function locCay<T extends { tenDayDu: string; tu: U[] }, U extends { name: string; code: string | null; den: DenTimDuoc[] }>(cay: T[], q: string): T[] {
  const k = boDau(q.trim());
  if (!k) return cay;
  const khop = (s: string | null | undefined) => !!s && boDau(s).includes(k);
  return cay.flatMap((r) => {
    if (khop(r.tenDayDu)) return [r];
    const tu = r.tu.flatMap((t) => {
      if (khop(t.name) || khop(t.code)) return [t];
      const den = t.den.filter((d) => khop(tenDenThuan(d)) || khop(d.name) || boDau(d.addr.toString(16)).includes(k));
      return den.length ? [{ ...t, den }] : [];
    });
    return tu.length ? [{ ...r, tu }] : [];
  });
}

/** Giờ ngõ bật hôm nay theo LỊCH TỦ ĐANG CHẠY (TK-37 `dangChay`), hợp các ngõ; chưa biết lịch ⇒ null. */
export function phutBatHomNay(lich: NgoLich[] | null, mt: { sunsetMin: number | null; sunriseMin: number | null }, now: number): number | null {
  if (!lich) return null;
  const dai = daiCapDien(lich, lich.map((o) => o.index), ngayVN(now), mt);
  return dai === null ? null : dai.reduce((n, [a, b]) => n + (b - a), 0);
}

